/**
 * JudgmentPattern Management Page
 * Task 17.6: JudgmentPattern管理UIの構築
 * Requirements: 22.1, 22.2, 22.3, 22.4, 22.5, 22.6, 22.7, 22.8, 22.9, 22.10, 22.11
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
import {
  getPatterns,
  getPatternById,
  createPattern,
  updatePattern,
  deletePattern,
  exportPattern,
  importPattern,
  bulkImportPatterns,
  JudgmentPattern,
  GetPatternsParams,
  CreatePatternInput,
  BulkImportMode,
  BulkImportResult,
} from '../api/judgmentPatternApi';
import {
  convertCsvToJson,
  downloadJsonFile,
  ConversionResult,
  JudgmentPatternJson,
} from '../utils/csvToJsonConverter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function JudgmentPatternManagementPage() {
  const [patterns, setPatterns] = useState<JudgmentPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [nameFilter, setNameFilter] = useState('');

  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<JudgmentPattern | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<CreatePatternInput>>({
    scoreConditions: {},
  });
  const [tabValue, setTabValue] = useState(0);

  // CSV to JSON conversion state
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvConversionResult, setCsvConversionResult] = useState<ConversionResult | null>(null);
  const [convertedPatterns, setConvertedPatterns] = useState<JudgmentPatternJson[]>([]);

  // Bulk import state
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportMode, setBulkImportMode] = useState<BulkImportMode>('add');
  const [patternsToImport, setPatternsToImport] = useState<CreatePatternInput[]>([]);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResult | null>(null);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);

  // Load patterns
  const loadPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetPatternsParams = {
        name: nameFilter || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      const response = await getPatterns(params);
      setPatterns(response.patterns);
      setTotalCount(response.pagination.total);
    } catch (err) {
      setError('Failed to load patterns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatterns();
  }, [page, rowsPerPage, nameFilter]);

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // View pattern
  const handleView = async (id: string) => {
    try {
      const pattern = await getPatternById(id);
      setSelectedPattern(pattern);
      setViewDialogOpen(true);
    } catch (err) {
      setError('Failed to load pattern details');
      console.error(err);
    }
  };

  // Create pattern
  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      scoreConditions: {},
    });
    setTabValue(0);
    setCreateDialogOpen(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.name || !formData.description) {
      setError('Name and description are required');
      return;
    }

    try {
      await createPattern(formData as CreatePatternInput);
      setCreateDialogOpen(false);
      setFormData({ scoreConditions: {} });
      loadPatterns();
    } catch (err) {
      setError('Failed to create pattern');
      console.error(err);
    }
  };

  // Edit pattern
  const handleEdit = async (id: string) => {
    try {
      const pattern = await getPatternById(id);
      setSelectedPattern(pattern);
      // Convert null to undefined for form data
      const formDataConverted = {
        ...pattern,
        desc11: pattern.desc11 ?? undefined,
        desc12: pattern.desc12 ?? undefined,
        desc13: pattern.desc13 ?? undefined,
        desc14: pattern.desc14 ?? undefined,
        desc15: pattern.desc15 ?? undefined,
        desc16: pattern.desc16 ?? undefined,
        desc21: pattern.desc21 ?? undefined,
        desc22: pattern.desc22 ?? undefined,
        desc23: pattern.desc23 ?? undefined,
        desc24: pattern.desc24 ?? undefined,
        desc25: pattern.desc25 ?? undefined,
        desc31: pattern.desc31 ?? undefined,
        desc32: pattern.desc32 ?? undefined,
        desc33: pattern.desc33 ?? undefined,
        desc34: pattern.desc34 ?? undefined,
        desc35: pattern.desc35 ?? undefined,
        desc41: pattern.desc41 ?? undefined,
        desc42: pattern.desc42 ?? undefined,
        desc43: pattern.desc43 ?? undefined,
        desc44: pattern.desc44 ?? undefined,
        desc45: pattern.desc45 ?? undefined,
        desc46: pattern.desc46 ?? undefined,
        desc47: pattern.desc47 ?? undefined,
      };
      setFormData(formDataConverted);
      setTabValue(0);
      setEditDialogOpen(true);
    } catch (err) {
      setError('Failed to load pattern details');
      console.error(err);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedPattern) return;

    try {
      await updatePattern(selectedPattern.id, formData);
      setEditDialogOpen(false);
      setSelectedPattern(null);
      setFormData({ scoreConditions: {} });
      loadPatterns();
    } catch (err) {
      setError('Failed to update pattern');
      console.error(err);
    }
  };

  // Delete pattern
  const handleDeleteClick = (pattern: JudgmentPattern) => {
    setSelectedPattern(pattern);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPattern) return;

    try {
      await deletePattern(selectedPattern.id);
      setDeleteDialogOpen(false);
      setSelectedPattern(null);
      loadPatterns();
    } catch (err) {
      setError('Failed to delete pattern');
      console.error(err);
    }
  };

  // Export pattern
  const handleExport = async (id: string) => {
    try {
      const jsonData = await exportPattern(id);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `judgment-pattern-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export pattern');
      console.error(err);
    }
  };

  // Import pattern (single JSON)
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        await importPattern(text);
        loadPatterns();
      } catch (err) {
        setError('Failed to import pattern');
        console.error(err);
      }
    };
    input.click();
  };

  // CSV to JSON conversion
  const handleCsvToJson = () => {
    console.log('[CSV] handleCsvToJson called');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      console.log('[CSV] File selected');
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        console.log('[CSV] No file');
        return;
      }

      try {
        console.log('[CSV] Reading:', file.name, file.size, 'bytes');
        const text = await file.text();
        console.log('[CSV] Content length:', text.length);
        
        const result = convertCsvToJson(text);
        console.log('[CSV] Result:', result.successCount, 'success,', result.errorCount, 'errors');
        
        setCsvConversionResult(result);
        setConvertedPatterns(result.patterns);
        setCsvDialogOpen(true);
        console.log('[CSV] Dialog opened');
      } catch (err) {
        console.error('[CSV] Error:', err);
        setError('Failed to read CSV file');
      }
    };
    input.click();
  };

  // Download converted JSON
  const handleDownloadJson = () => {
    if (convertedPatterns.length > 0) {
      downloadJsonFile(convertedPatterns, 'judgment-patterns.json');
    }
  };

  // Open bulk import dialog with converted patterns
  const handleOpenBulkImport = () => {
    setPatternsToImport(convertedPatterns as CreatePatternInput[]);
    setBulkImportResult(null);
    setCsvDialogOpen(false);
    setBulkImportDialogOpen(true);
  };

  // Bulk import from JSON file
  const handleBulkImportFromFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const patterns = JSON.parse(text);
        if (!Array.isArray(patterns)) {
          setError('JSON file must contain an array of patterns');
          return;
        }
        setPatternsToImport(patterns);
        setBulkImportResult(null);
        setBulkImportDialogOpen(true);
      } catch (err) {
        setError('Failed to read JSON file');
        console.error(err);
      }
    };
    input.click();
  };

  // Bulk import progress state
  const [bulkImportProgress, setBulkImportProgress] = useState<{
    current: number;
    total: number;
    phase: string;
  } | null>(null);

  // Execute bulk import with batching
  const handleExecuteBulkImport = async () => {
    if (patternsToImport.length === 0) return;

    setBulkImportLoading(true);
    setBulkImportResult(null);
    setError(null);

    const BATCH_SIZE = 100;
    const totalBatches = Math.ceil(patternsToImport.length / BATCH_SIZE);
    
    let totalSuccessCount = 0;
    let totalErrorCount = 0;
    const allErrors: BulkImportResult['errors'] = [];
    let deletedCount: number | undefined;

    try {
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const start = batchIndex * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, patternsToImport.length);
        const batch = patternsToImport.slice(start, end);
        
        setBulkImportProgress({
          current: batchIndex + 1,
          total: totalBatches,
          phase: `バッチ ${batchIndex + 1}/${totalBatches} を処理中... (${start + 1}-${end}件目)`,
        });

        // Only use 'replace' mode for the first batch, then 'add' for subsequent batches
        const mode = batchIndex === 0 ? bulkImportMode : 'add';
        
        try {
          const result = await bulkImportPatterns(batch, mode);
          totalSuccessCount += result.successCount;
          totalErrorCount += result.errorCount;
          
          // Adjust error indices to reflect original position
          const adjustedErrors = result.errors.map(err => ({
            ...err,
            index: err.index + start,
          }));
          allErrors.push(...adjustedErrors);
          
          // Capture deletedCount from first batch if in replace mode
          if (batchIndex === 0 && result.deletedCount !== undefined) {
            deletedCount = result.deletedCount;
          }
        } catch (batchErr) {
          console.error(`Batch ${batchIndex + 1} failed:`, batchErr);
          // Add error for all items in this batch
          for (let i = 0; i < batch.length; i++) {
            allErrors.push({
              index: start + i,
              name: batch[i].name || `(row ${start + i + 1})`,
              error: batchErr instanceof Error ? batchErr.message : 'バッチ処理エラー',
            });
          }
          totalErrorCount += batch.length;
        }
      }

      const finalResult: BulkImportResult = {
        success: allErrors.length === 0,
        totalCount: patternsToImport.length,
        successCount: totalSuccessCount,
        errorCount: totalErrorCount,
        errors: allErrors,
        deletedCount,
      };

      setBulkImportResult(finalResult);
      setBulkImportProgress(null);
      
      if (finalResult.successCount > 0) {
        loadPatterns();
      }
    } catch (err) {
      console.error('Bulk import failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`バルクインポートに失敗しました: ${errorMessage}`);
    } finally {
      setBulkImportLoading(false);
      setBulkImportProgress(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">JudgmentPattern Management</Typography>
        <Box>
          <Button startIcon={<TransformIcon />} onClick={handleCsvToJson} sx={{ mr: 1 }}>
            CSV→JSON
          </Button>
          <Button startIcon={<UploadIcon />} onClick={handleBulkImportFromFile} sx={{ mr: 1 }}>
            Bulk Import
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            Create Pattern
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Pattern Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
          />
          <Button variant="outlined" onClick={() => setNameFilter('')}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : patterns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No patterns found
                </TableCell>
              </TableRow>
            ) : (
              patterns.map((pattern) => (
                <TableRow key={pattern.id}>
                  <TableCell>{pattern.name}</TableCell>
                  <TableCell>{pattern.description.substring(0, 50)}...</TableCell>
                  <TableCell>{new Date(pattern.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(pattern.id)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(pattern.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleExport(pattern.id)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(pattern)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pattern Details</DialogTitle>
        <DialogContent>
          {selectedPattern && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="ID" value={selectedPattern.id} InputProps={{ readOnly: true }} />
              <TextField label="Name" value={selectedPattern.name} InputProps={{ readOnly: true }} />
              <TextField
                label="Description"
                value={selectedPattern.description}
                multiline
                rows={3}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Score Conditions (JSON)"
                value={JSON.stringify(selectedPattern.scoreConditions, null, 2)}
                multiline
                rows={5}
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{createDialogOpen ? 'Create Pattern' : 'Edit Pattern'}</DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" />
            <Tab label="Group 1 (desc1x)" />
            <Tab label="Group 2 (desc2x)" />
            <Tab label="Group 3 (desc3x)" />
            <Tab label="Group 4 (desc4x)" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Name *"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                label="Description *"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
              <TextField
                label="Score Conditions (JSON) *"
                value={JSON.stringify(formData.scoreConditions || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({ ...formData, scoreConditions: parsed });
                  } catch (err) {
                    // Invalid JSON, ignore
                  }
                }}
                multiline
                rows={5}
                helperText='Example: {"A": {"min": 10, "max": 20}}'
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="desc11"
                value={formData.desc11 || ''}
                onChange={(e) => setFormData({ ...formData, desc11: e.target.value })}
              />
              <TextField
                fullWidth
                label="desc12"
                value={formData.desc12 || ''}
                onChange={(e) => setFormData({ ...formData, desc12: e.target.value })}
              />
              <TextField
                fullWidth
                label="desc13"
                value={formData.desc13 || ''}
                onChange={(e) => setFormData({ ...formData, desc13: e.target.value })}
              />
              <TextField
                fullWidth
                label="desc14"
                value={formData.desc14 || ''}
                onChange={(e) => setFormData({ ...formData, desc14: e.target.value })}
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="desc15"
                value={formData.desc15 || ''}
                onChange={(e) => setFormData({ ...formData, desc15: e.target.value })}
              />
              <TextField
                fullWidth
                label="desc16"
                value={formData.desc16 || ''}
                onChange={(e) => setFormData({ ...formData, desc16: e.target.value })}
                multiline
                rows={3}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['desc21', 'desc22', 'desc24'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                />
              ))}
              {['desc23', 'desc25'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={3}
                />
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['desc31', 'desc32', 'desc34'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                />
              ))}
              {['desc33', 'desc35'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={3}
                />
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['desc41', 'desc42', 'desc44', 'desc46'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                />
              ))}
              {['desc43', 'desc45', 'desc47'].map((field) => (
                <TextField
                  key={field}
                  fullWidth
                  label={field}
                  value={(formData as any)[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={3}
                />
              ))}
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={createDialogOpen ? handleSaveCreate : handleSaveEdit}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete pattern "{selectedPattern?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV to JSON Conversion Dialog */}
      <Dialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>CSV → JSON 変換結果</DialogTitle>
        <DialogContent>
          {csvConversionResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  label={`総行数: ${csvConversionResult.totalRows}`}
                  color="default"
                />
                <Chip
                  label={`成功: ${csvConversionResult.successCount}`}
                  color="success"
                />
                <Chip
                  label={`エラー: ${csvConversionResult.errorCount}`}
                  color={csvConversionResult.errorCount > 0 ? 'error' : 'default'}
                />
              </Box>

              {csvConversionResult.errors.length > 0 && (
                <Alert severity="warning">
                  <Typography variant="subtitle2">変換エラー:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {csvConversionResult.errors.slice(0, 10).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {csvConversionResult.errors.length > 10 && (
                      <li>...他 {csvConversionResult.errors.length - 10} 件のエラー</li>
                    )}
                  </Box>
                </Alert>
              )}

              {csvConversionResult.successCount > 0 && (
                <Alert severity="success">
                  {csvConversionResult.successCount} 件のパターンが正常に変換されました。
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary">
                変換されたデータをJSONファイルとしてダウンロードするか、
                直接バルクインポートを実行できます。
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>閉じる</Button>
          <Button
            onClick={handleDownloadJson}
            startIcon={<DownloadIcon />}
            disabled={convertedPatterns.length === 0}
          >
            JSONダウンロード
          </Button>
          <Button
            onClick={handleOpenBulkImport}
            variant="contained"
            disabled={convertedPatterns.length === 0}
          >
            バルクインポートへ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog
        open={bulkImportDialogOpen}
        onClose={() => !bulkImportLoading && setBulkImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>バルクインポート</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              {patternsToImport.length} 件のパターンをインポートします。
            </Alert>

            <FormControl component="fieldset">
              <FormLabel component="legend">インポートモード</FormLabel>
              <RadioGroup
                value={bulkImportMode}
                onChange={(e) => setBulkImportMode(e.target.value as BulkImportMode)}
              >
                <FormControlLabel
                  value="add"
                  control={<Radio />}
                  label="追加 - 既存のパターンを保持し、新しいパターンを追加"
                />
                <FormControlLabel
                  value="replace"
                  control={<Radio />}
                  label="総入れ替え - 既存のパターンをすべて削除し、新しいパターンで置き換え"
                />
              </RadioGroup>
            </FormControl>

            {bulkImportMode === 'replace' && (
              <Alert severity="warning">
                警告: 総入れ替えモードでは、既存のすべてのパターンが削除されます。
                この操作は取り消せません。
              </Alert>
            )}

            {bulkImportLoading && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress 
                  variant={bulkImportProgress ? 'determinate' : 'indeterminate'}
                  value={bulkImportProgress ? (bulkImportProgress.current / bulkImportProgress.total) * 100 : 0}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {bulkImportProgress ? bulkImportProgress.phase : 'インポート準備中...'}
                </Typography>
              </Box>
            )}

            {bulkImportResult && (
              <Box>
                {bulkImportResult.success ? (
                  <Alert severity="success">
                    インポート完了: {bulkImportResult.successCount} 件成功
                    {bulkImportResult.deletedCount !== undefined && (
                      <> (削除: {bulkImportResult.deletedCount} 件)</>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    インポート失敗: {bulkImportResult.successCount} 件成功, {bulkImportResult.errorCount} 件エラー
                    {bulkImportResult.errors.length > 0 && (
                      <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                        {bulkImportResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>
                            {err.name}: {err.error}
                          </li>
                        ))}
                        {bulkImportResult.errors.length > 5 && (
                          <li>...他 {bulkImportResult.errors.length - 5} 件のエラー</li>
                        )}
                      </Box>
                    )}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkImportDialogOpen(false)}
            disabled={bulkImportLoading}
          >
            閉じる
          </Button>
          <Button
            onClick={handleExecuteBulkImport}
            variant="contained"
            color={bulkImportMode === 'replace' ? 'error' : 'primary'}
            disabled={bulkImportLoading || patternsToImport.length === 0}
          >
            {bulkImportMode === 'replace' ? '総入れ替え実行' : 'インポート実行'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
